import {
  all,
  call,
  fork,
  put,
  select,
  takeLatest,
  takeEvery
} from 'redux-saga/effects';
import { notification } from 'antd';

import { showExportReadyNotification } from '../../pages/common/components/ExportReadyNotification';
import {
  getFramlCustomers,
  getFramlCustomerAlerts
} from '../../services/framl/landing';
import {
  createCaseFromAlerts,
  mergeAlertsToCase
} from '../../services/cm/alerts';
import { buildBulkCaseOutcome } from '../../pages/framl/landing/bulkCaseOutcome';
import { reloadCases } from '../../redux/actions/cm/caseList';
import { reloadAlerts } from '../../redux/actions/cm/alertList';
import * as ga from '../../redux/actions/app';
import * as tmApi from '../../services/tmCommon';
import {
  fetchQueueSuccess,
  fetchQueueFailure,
  setPage,
  fetchCustomerAlertsSuccess,
  fetchCustomerAlertsFailure,
  downloadFramlQueueExportSuccess,
  downloadFramlQueueExportFail,
  bulkCaseOutcome,
  fetchCustomerAlerts,
  fetchQueue,
  CREATE_CASES_FOR_CUSTOMERS,
  MERGE_CASES_FOR_CUSTOMERS,
  FETCH_QUEUE,
  FETCH_CUSTOMER_ALERTS,
  SET_FILTERS,
  SET_SORT,
  DOWNLOAD_FRAML_QUEUE_EXPORT
} from '../../redux/actions/framl/landing';
import {
  getFilters,
  getSort,
  getPagination
} from '../../redux/selectors/framl/landing';

const tmNetworkLayer = new tmApi.NetworkLayer();

// FIN-13017 (clamp half) — the last page of a SHRINKING result set.
//
// The reset half (already shipped) covers a control change: search, filters, sort and the tiles
// all return to page 0. It does NOT cover the other way in: the page stays put while the RESULT
// SET shrinks underneath it — the 3-minute auto-refresh, or another analyst resolving rows. The
// offset then overshoots a queue that is now shorter, and the grid renders empty with matching
// rows still available on an earlier page. Same symptom as the reported bug, different trigger.
//
// Clamped here rather than in the reducer for two reasons: the reducer cannot refetch, so a
// clamp there would fix the pager while leaving the grid empty until the next user action; and
// keeping it in the saga makes the extra round-trip explicit rather than a hidden effect.
//
// Bounded to exactly ONE extra fetch and cannot loop: lastPage is derived from the total the
// server just returned, so the retry requests a page that is valid by construction, and the
// retry itself is issued through fetchQueuePage rather than re-entering this worker.
const lastPageFor = ({ total = 0, size = 10 } = {}) =>
  size > 0 ? Math.max(0, Math.ceil(total / size) - 1) : 0;

export function* fetchQueueWorker() {
  try {
    const filters = yield select(getFilters);
    const sort = yield select(getSort);
    const pagination = yield select(getPagination);
    const params = { ...filters, ...sort, ...pagination };
    const response = yield call(getFramlCustomers, params);

    const requestedPage = pagination?.page ?? 0;
    const returned = response?.pagination || {};
    const total = returned.total ?? 0;
    // The size the SERVER actually applied wins over the one we asked for — it may cap or
    // default it. `lastPage` and the retry below must both be computed from this same value,
    // or the clamp lands on a page derived for one page size and fetched at another and can
    // overshoot a second time (review note, #11709).
    const effectiveSize = returned.size ?? pagination?.size ?? 10;
    const lastPage = lastPageFor({ total, size: effectiveSize });
    const rows = response?.customers || [];

    // Overshoot: the server had rows to give, this page had none, and an earlier page holds
    // them. `rows.length === 0` is part of the test on purpose — a full page that merely sits
    // beyond `lastPage` by a rounding difference must not trigger a refetch.
    if (rows.length === 0 && total > 0 && requestedPage > lastPage) {
      // Order matters: the retry is issued FIRST and the page index is committed only if it
      // succeeds. Dispatching setPage before the call left the store describing a page the grid
      // was not showing whenever the retry failed — the empty response is never committed, so
      // `customers` still held the page the user came from while the pager had already moved to
      // `lastPage`. A 500 on the retry then rendered "page 2" over page-1 rows until the next
      // poll. Raised in review on #11709.
      const clamped = yield call(getFramlCustomers, {
        ...filters,
        ...sort,
        ...pagination,
        size: effectiveSize,
        page: lastPage
      });
      yield put(setPage(lastPage));
      yield put(fetchQueueSuccess(clamped));
      return;
    }

    yield put(fetchQueueSuccess(response));
  } catch (e) {
    // Keep the HTTP status so the page can tell a 403 (no view-all-depts scope)
    // apart from a genuine empty queue. FIN-11360 F1.
    yield put(
      fetchQueueFailure({
        status: e?.response?.status ?? null,
        message: e.message
      })
    );
  }
}

export function* fetchCustomerAlertsWorker(action) {
  // FIN-12887: payload is { customerKey, page, size }; tolerate the legacy
  // bare-string payload (older dispatch sites / queued actions).
  // FIN-13005 — hoisted out of the try so the catch can attribute the failure to a customer.
  const p = action.payload;
  const customerKey = typeof p === 'string' ? p : p.customerKey;
  try {
    const page = typeof p === 'string' ? 0 : p.page ?? 0;
    const size = typeof p === 'string' ? 20 : p.size ?? 20;
    const envelope = yield call(
      getFramlCustomerAlerts,
      customerKey,
      page,
      size
    );
    yield put(
      fetchCustomerAlertsSuccess(customerKey, envelope.items || [], {
        total: envelope.total,
        page: envelope.page,
        size: envelope.size
      })
    );
  } catch (e) {
    // FIN-13005 — still must not kill the saga root, but the failure has to REACH the store.
    // Swallowing it made a 500 indistinguishable from an empty snapshot: the panel showed
    // "No underlying alerts" and the header counted (0), which is what QA reported against a
    // row whose chip said Fraud 11 / AML 3.
    yield put(fetchCustomerAlertsFailure(customerKey, e));
  }
}

// FIN-11360 — FRAML View queue CSV export. Mirrors
// sagas/tmCommon.js:93-129 (amlsTMCommonTransactionHistoryDownload):
// current-page path resolves a blob download; "download all" path returns a
// queued-job response and surfaces an "Export In Progress" toast.
//
// FIN-13175 — `onSuccess` is a caller-supplied completion hook (Download History
// refresh), same contract as sagas/tmCommon.js:102-105. Stripped before the API call
// (framlQueueExportAPI must never receive a function in its payload) and invoked only
// after the submission succeeded, so FramlLandingPage's refreshKey bump can never race
// the BE job-row write.
export function* framlQueueExportDownload({ onSuccess, ...payload }) {
  try {
    yield put(ga.fetchRequest({ text: 'fetching...' }));
    const response = yield call(tmNetworkLayer.framlQueueExportAPI, payload);

    if (payload.downloadAll) {
      // FIN-13177 — the BE already de-dupes by filter hash and every submit response
      // carries { isExisting, status }; this queue never read them, so a repeat export
      // fell straight into the generic "Export In Progress" toast even when the file was
      // already COMPLETED and ready — the self-contradictory toast from FIN-13511's
      // screenshot. crsRiskDetail/AssocTransaction/index.js:283 is the one place in the FE
      // that already reads isExisting; mirrored here rather than reinvented.
      if (response?.isExisting && response?.status === 'COMPLETED') {
        showExportReadyNotification(response);
      } else {
        notification.info({
          message: 'Export In Progress',
          description:
            response?.message ||
            "Your export is being prepared. You will be notified when it's ready."
        });
      }
    }

    yield put(downloadFramlQueueExportSuccess(response));
    yield put(ga.fetchSuccess());
    if (typeof onSuccess === 'function') onSuccess(response);
  } catch (error) {
    yield put(ga.fetchFailed({ error: '' }));
    yield put(downloadFramlQueueExportFail({ error }));
  }
}

export function* watchFramlQueueExportDownload() {
  yield takeEvery(DOWNLOAD_FRAML_QUEUE_EXPORT, function* ({ payload }) {
    yield fork(framlQueueExportDownload, payload);
  });
}

// FIN-13958 — one BE request per customer, never thrown: each settles to { ok, res | error } so a
// failing customer cannot abort (or hide) the others.
export function* settlePlan(fn, plan, args) {
  try {
    const res = yield call(fn, args);
    return {
      customerKey: plan.customerKey,
      alertIds: plan.alertIds,
      ok: true,
      res
    };
  } catch (error) {
    return {
      customerKey: plan.customerKey,
      alertIds: plan.alertIds,
      ok: false,
      error
    };
  }
}

// The involved customers' cached alerts are stale after ANY outcome (caseId just changed, or a 409
// proved the cache was already stale). They are refetched — and WAITED for — BEFORE the outcome is
// reported, so when the page re-enables Create Case the eligible counts are already fresh and a fast
// retry cannot re-send the same cased alerts (review L4). The queue refetch is fire-and-forget.
function* refreshCustomers(plans) {
  yield all(
    (plans || []).map((p) =>
      call(
        fetchCustomerAlertsWorker,
        fetchCustomerAlerts(p.customerKey, 0, 100)
      )
    )
  );
  yield put(fetchQueue());
}

// Review R1 — the CM-slice flow used to dispatch CREATE_CASE_FROM_ALERT_SUCCESS /
// MERGE_ALERTS_TO_CASE_SUCCESS, which also mark the CM Case List and Alert List stale
// (reducers/cm/caseList + alertList → hasLoaded: false). Calling the services directly skips that,
// so invalidate both lists explicitly whenever something was actually created / merged.
function* invalidateCmLists(outcome) {
  if ((outcome.created || []).length > 0 || outcome.merged > 0) {
    yield put(reloadCases());
    yield put(reloadAlerts());
  }
}

// Review L5 — never let an unexpected throw escape: it would kill this watcher (queue, filters and
// expand fetches with it) and leave the in-flight lock set. Always report an outcome.
function* runBulkCase(kind, rawPlans, run, extra) {
  const plans = Array.isArray(rawPlans) ? rawPlans : [];
  let outcome;
  try {
    const results = yield all(plans.map(run));
    outcome = buildBulkCaseOutcome(kind, results, {
      ...extra,
      customers: plans.length
    });
  } catch (error) {
    outcome = buildBulkCaseOutcome(
      kind,
      [{ customerKey: null, ok: false, error }],
      { ...extra, customers: plans.length }
    );
  }
  try {
    yield call(refreshCustomers, plans);
  } catch (e) {
    // a failed refresh must not swallow the outcome; per-customer failures are already recorded
  }
  yield call(invalidateCmLists, outcome);
  yield put(bulkCaseOutcome(outcome));
}

export function* createCasesForCustomersWorker({ payload }) {
  const { plans = [], departmentId, caseType, alreadyCased } = payload || {};
  yield call(
    runBulkCase,
    'create',
    plans,
    (p) =>
      call(settlePlan, createCaseFromAlerts, p, {
        alertIds: p.alertIds,
        departmentId
      }),
    { caseType, alreadyCased }
  );
}

export function* mergeCasesForCustomersWorker({ payload }) {
  const { plans = [], alreadyCased } = payload || {};
  yield call(
    runBulkCase,
    'merge',
    plans,
    (p) =>
      call(settlePlan, mergeAlertsToCase, p, {
        alertIds: p.alertIds,
        caseId: p.caseId
      }),
    { alreadyCased }
  );
}

export default function* watchFramlLanding() {
  yield takeLatest(FETCH_QUEUE, fetchQueueWorker);
  // takeEvery (not takeLatest): bulk actions dispatch one fetch per selected
  // customer in a synchronous loop — takeLatest would cancel all but the last,
  // so the modal-open effect (.every(loaded)) never fires. See landing.test.js.
  yield takeEvery(FETCH_CUSTOMER_ALERTS, fetchCustomerAlertsWorker);
  yield takeLatest(SET_FILTERS, fetchQueueWorker);
  yield takeLatest(SET_SORT, fetchQueueWorker);
  yield fork(watchFramlQueueExportDownload);
  // takeEvery, not takeLatest: takeLatest cancelled a running worker AFTER its requests were sent, so
  // its outcome was lost and the retry 409'd against the case it had just merged into (review R2).
  // Re-entry is prevented by the page's per-kind in-flight lock instead.
  yield takeEvery(CREATE_CASES_FOR_CUSTOMERS, createCasesForCustomersWorker);
  yield takeEvery(MERGE_CASES_FOR_CUSTOMERS, mergeCasesForCustomersWorker);
}
